// =============================================================================
// MOB-16 / C3-b (2026-07-14) — WebAuthn passkeys (Face ID / Touch ID)
// =============================================================================
// Browser-side helpers for the passkey ceremonies. The backend
// (routes/webauthn.py) hands us options with base64url-encoded challenge /
// credential ids; navigator.credentials.{create,get} want ArrayBuffers, and
// their results come back as ArrayBuffers which we re-encode to base64url for
// py_webauthn to verify. All the fiddly conversion lives here so the UI just
// calls signInWithPasskey / enrollPasskey.
//
// Staff-only is enforced server-side; this module makes no role assumptions.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- base64url <-> ArrayBuffer ----------------------------------------------
function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlToBuf(s) {
  const norm = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = norm.length % 4 ? "=".repeat(4 - (norm.length % 4)) : "";
  const bin = atob(norm + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

// --- Capability probe --------------------------------------------------------
// True only when this browser has a platform authenticator (Face ID / Touch ID
// / Windows Hello) actually available — not just the WebAuthn API present.
export async function isPlatformPasskeyAvailable() {
  try {
    if (
      typeof window === "undefined" ||
      !window.PublicKeyCredential ||
      !navigator.credentials ||
      typeof navigator.credentials.create !== "function"
    ) {
      return false;
    }
    if (!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_e) {
    return false;
  }
}

// --- Sign in with a passkey --------------------------------------------------
// Runs options -> navigator.credentials.get() -> verify. Returns the backend
// verify payload { success, token, user }. Must be called from inside a user
// gesture (iOS requirement).
//
// C3-c: email is OPTIONAL. With no email the credentials are discoverable
// (resident keys), so the authenticator surfaces the passkey itself and the
// server resolves the account from it — the user never types an email. The
// server returns a single-use login_handle keyed to the challenge, which we
// echo back on verify.
// STAFF-MFA-1 (30 Oct 2026) — pass `ticket` (from a password sign-in) to use
// the passkey as the second step; `rememberDevice` then skips the second step
// on this device for 30 days.
export async function signInWithPasskey(email, { ticket, rememberDevice } = {}) {
  const { data } = await axios.post(`${API}/webauthn/login/options`, { email: email || "" });
  const { login_handle, options } = data;

  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };

  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error("No passkey selected");

  const credential = {
    id: assertion.id,
    rawId: bufToB64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufToB64url(assertion.response.clientDataJSON),
      authenticatorData: bufToB64url(assertion.response.authenticatorData),
      signature: bufToB64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufToB64url(assertion.response.userHandle)
        : null,
    },
    clientExtensionResults: assertion.getClientExtensionResults?.() || {},
  };

  const { data: result } = await axios.post(`${API}/webauthn/login/verify`, {
    login_handle,
    credential,
    ...(ticket ? { ticket, remember_device: !!rememberDevice } : {}),
  });
  return result;
}

// --- Enrol a passkey on this device -----------------------------------------
// Auth'd staff only (backend gates). Returns the created credential summary.
export async function enrollPasskey(deviceLabel) {
  const { data: options } = await axios.post(`${API}/webauthn/register/options`, {});

  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("Enrolment cancelled");

  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      attestationObject: bufToB64url(cred.response.attestationObject),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };

  const { data } = await axios.post(`${API}/webauthn/register/verify`, {
    credential,
    device_label: deviceLabel || defaultDeviceLabel(),
  });
  return data;
}

export async function listPasskeys() {
  const { data } = await axios.get(`${API}/webauthn/credentials`);
  return data.credentials || [];
}

export async function deletePasskey(id) {
  await axios.delete(`${API}/webauthn/credentials/${id}`);
}

// Best-effort friendly label from the UA so the device list is readable.
export function defaultDeviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android device";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
}
